import React, { useState, useEffect, useMemo, useCallback } from "react";
import Table from "../../components/UI/Table";
import PropTypes from "prop-types";
import { request } from "../../utils/api";
import { displayDateUS } from "../../utils/dateUtils";
import toast from "react-hot-toast";
import {
  EditIcon,
  DeleteIcon,
  SearchIcon,
  PlusIcon,
  CloseIcon,
  ChevronDownIcon,
} from "../../Icons";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getSessionTenantId } from "../../utils/tenantContext";
import DeleteConfirmModal from "../Documents/components/DeleteConfirmModal";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_ACTIONS,
  CELL_MUTED,
  CELL_STRONG,
} from "../../ui/pageChrome";
import {
  EVALUATION_THEME,
  EVALUATION_BTN,
  EVALUATION_COL,
  EVALUATION_FIELD,
  EVALUATION_ROLE_CHIP,
} from "./evaluationTheme";
import DocumentBulkActions from "../Documents/components/DocumentBulkActions";
import { useRowSelection } from "../Documents/hooks/useRowSelection";
import {
  buildBulkPrintHtml,
  isRowPrintable,
  openDocumentsPrintTab,
} from "../../print/documentPrintService";

/** `id` is only unique per evaluation table (RN, PT, LVN, …), not across the combined list. */
const getEvaluationRowKey = (row) => {
  const t = String(row?.type ?? "").toUpperCase() || "UNKNOWN";
  return `${t}-${row?.id}`;
};

const TYPE_FILTERS = [
  { value: "", label: "All Types" },
  { value: "RN", label: "RN" },
  { value: "LVN", label: "LVN" },
  { value: "PT", label: "PT" },
  { value: "PTA", label: "PTA" },
  { value: "OT", label: "OT" },
  { value: "OTA", label: "OTA" },
  { value: "ST", label: "ST" },
  { value: "MSW", label: "MSW" },
  { value: "HHA", label: "HHA" },
  { value: "OA", label: "OA" },
];

const STATUS_FILTERS = [
  { value: "", label: "All Status" },
  { value: "completed", label: "Completed" },
  { value: "draft", label: "Draft" },
];

const EvaluationHistory = ({ onEdit, onBack }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkPrintLoading, setBulkPrintLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [evaluationListSort, setEvaluationListSort] = useState({
    sortKey: "evaluationDate",
    sortOrder: "desc",
  });

  const toggleEvaluationSort = useCallback((key) => {
    setEvaluationListSort((prev) => ({
      sortKey: key,
      sortOrder:
        prev.sortKey === key && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  }, []);

  const hasActiveFilters = Boolean(
    searchTerm.trim() || typeFilter || statusFilter,
  );

  const clearFilters = () => {
    setSearchTerm("");
    setTypeFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const filteredData = useMemo(() => {
    return (data || []).filter((row) => {
      if (typeFilter) {
        const t = String(row.type || "").toUpperCase();
        if (t !== typeFilter.toUpperCase()) return false;
      }
      if (statusFilter) {
        const s = String(row.status || "draft").toLowerCase();
        if (s !== statusFilter.toLowerCase()) return false;
      }
      return true;
    });
  }, [data, typeFilter, statusFilter]);

  const displayedEvaluationsList = useMemo(() => {
    const { sortKey, sortOrder } = evaluationListSort;
    const result = [...filteredData];
    if (!sortKey) return result;

    const getSortValue = (row, key) => {
      if (key === "employeeName") {
        return `${row.employee?.firstName || ""} ${row.employee?.lastName || ""}`.trim();
      }
      let val = row[key];
      if (val === null || val === undefined) val = "";
      return val;
    };

    const tieBreak = (a, b) =>
      String(a.__rowKey ?? getEvaluationRowKey(a)).localeCompare(
        String(b.__rowKey ?? getEvaluationRowKey(b)),
      );

    return result.sort((a, b) => {
      let valA = getSortValue(a, sortKey);
      let valB = getSortValue(b, sortKey);

      if (valA === null || valA === undefined) valA = "";
      if (valB === null || valB === undefined) valB = "";

      let cmp = 0;

      if (typeof valA === "number" && typeof valB === "number") {
        cmp = sortOrder === "asc" ? valA - valB : valB - valA;
      } else {
        const isDate = (val) =>
          typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val);
        if (isDate(valA) && isDate(valB)) {
          const dateA = new Date(valA).getTime();
          const dateB = new Date(valB).getTime();
          cmp = sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        } else {
          const strA = valA.toString().toLowerCase();
          const strB = valB.toString().toLowerCase();
          if (strA < strB) cmp = sortOrder === "asc" ? -1 : 1;
          else if (strA > strB) cmp = sortOrder === "asc" ? 1 : -1;
        }
      }

      if (cmp !== 0) return cmp;
      return tieBreak(a, b);
    });
  }, [filteredData, evaluationListSort]);

  const evaluationSortKey = evaluationListSort.sortKey;
  const evaluationSortOrder = evaluationListSort.sortOrder;

  const { tenantId: sessionTenantId, isAdmin, companyName, officeLogo } = useCurrentUser();
  const tenantId = sessionTenantId ?? getSessionTenantId();

  const DEFAULT_PRINT_LOGO = "/login-logo.png";

  const getPrintLogoAbsoluteUrl = useCallback(() => {
    const logo = officeLogo || "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!logo || !String(logo).trim()) {
      return `${origin}${DEFAULT_PRINT_LOGO}`;
    }
    if (logo.startsWith("http")) return logo;
    const normalized = logo.startsWith("/") ? logo : `/${logo}`;
    return `${origin}${normalized}`;
  }, [officeLogo]);

  const evaluationPrintable = useCallback(
    (row) => isRowPrintable(row, { kind: "evaluation" }),
    [],
  );

  const selection = useRowSelection(
    displayedEvaluationsList,
    getEvaluationRowKey,
    evaluationPrintable,
  );

  const getDeleteEndpoint = (type) => {
    if (!type) return "evaluation-rn";
    const t = type.toUpperCase();
    if (t === "RN") return "evaluation-rn";
    if (t === "PT" || t === "PTA") return "evaluation-pt-pta";
    if (t === "OT" || t === "OTA") return "evaluation-ot-ota";
    if (t === "HHA") return "evaluation-hha";
    if (t === "LVN") return "evaluation-lvn";
    if (t === "MSW") return "evaluation-msw";
    if (t === "ST") return "evaluation-st";
    if (t === "OA" || t === "OFFICE ASSISTANT" || t === "OFFICE-ASSISTANT")
      return "evaluation-oa";
    return "evaluation-rn"; // Default
  };

  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchEvaluations = async (pageNo, searchStr) => {
    setLoading(true);
    try {
      let url = `/evaluation-all?page=${pageNo}&limit=${limit}&tenantId=${tenantId}`;
      if (searchStr) {
        url += `&search=${encodeURIComponent(searchStr)}`;
      }

      const response = await request(url);
      if (response.status === 1 && response.result) {
        const evaluations = (response.result.evaluations || []).map((row) => ({
          ...row,
          __rowKey: getEvaluationRowKey(row),
        }));
        setData(evaluations);
        setTotalPages(response.result.totalPages || 1);
        setTotalItems(response.result.total || 0);
        setLimit(response.result.limit || 20);
      } else {
        toast.error(response.message || "Failed to fetch evaluations");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching evaluations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvaluations(page, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, evaluationSortKey, evaluationSortOrder]);

  const handleBulkPrint = async () => {
    if (!selection.selectedRows.length) {
      toast.error("Select at least one evaluation to print.");
      return;
    }

    setBulkPrintLoading(true);
    const toastId = toast.loading(
      selection.selectedRows.length === 1
        ? "Preparing evaluation for print…"
        : `Preparing ${selection.selectedRows.length} evaluations for print…`,
    );

    try {
      // Print every selected row. Missing / draft data → blank form layout.
      const rowsToPrint = selection.selectedRows;

      if (!rowsToPrint.length) {
        toast.error("No evaluations available to print.", {
          id: toastId,
        });
        return;
      }

      const { documents, printed, failed } = await buildBulkPrintHtml(rowsToPrint, {
        kind: "evaluation",
        branding: {
          companyName: companyName || "Company",
          logoUrl: getPrintLogoAbsoluteUrl(),
        },
        documentTitle: "Evaluation Forms",
      });

      if (!printed || !documents?.length) {
        toast.error(
          failed[0]?.reason || "No printable evaluations in selection.",
          { id: toastId },
        );
        return;
      }

      const opened = openDocumentsPrintTab(documents, {
        title: `Print ${printed} evaluation${printed === 1 ? "" : "s"}`,
        subtitle: "Evaluation History",
      });
      if (!opened) {
        toast.error("Pop-up blocked. Allow pop-ups to print.", { id: toastId });
        return;
      }

      if (failed.length) {
        toast.success(
          `Opened print preview for ${printed}. ${failed.length} skipped.`,
          { id: toastId },
        );
      } else {
        toast.success(
          printed === 1
            ? "Print preview opened. Use Print in the new tab."
            : `Print preview opened for ${printed} evaluations. Use Print in the new tab.`,
          { id: toastId },
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to prepare evaluation print.", { id: toastId });
    } finally {
      setBulkPrintLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    try {
      const endpoint = getDeleteEndpoint(itemToDelete.type);
      const response = await request(`/${endpoint}/${itemToDelete.id}`, {
        method: "DELETE",
      });

      if (response.status === 1) {
        toast.success("Evaluation deleted successfully");
        fetchEvaluations(page, debouncedSearch);
      } else {
        toast.error("Failed to delete evaluation");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting evaluation");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Type",
        accessor: "type",
        sortable: true,
        className: EVALUATION_COL.type,
        render: (row) => (
          <span className={`${CELL_STRONG} uppercase tracking-wide`}>
            {row.type || "—"}
          </span>
        ),
      },
      {
        header: "Employee",
        accessor: "employeeName",
        sortable: true,
        className: EVALUATION_COL.employee,
        render: (row) => (
          <div className="min-w-0">
            <div className={`${CELL_STRONG} truncate`}>
              {[row.employee?.firstName, row.employee?.lastName]
                .filter(Boolean)
                .join(" ") || "—"}
            </div>
            <div className={`${CELL_MUTED} mt-0.5 truncate`}>
              {row.employee?.email || "—"}
            </div>
          </div>
        ),
      },
      {
        header: "Position",
        accessor: "position",
        sortable: true,
        className: EVALUATION_COL.position,
        render: (row) => (
          <span
            className={`inline-flex items-center max-w-full h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide truncate border ${EVALUATION_ROLE_CHIP}`}
            title={row.position || row.type || ""}
          >
            {row.position || row.type || "—"}
          </span>
        ),
      },
      {
        header: "Evaluation Date",
        accessor: "evaluationDate",
        sortable: true,
        className: EVALUATION_COL.date,
        render: (row) => (
          <span className={`${CELL_MUTED} tabular-nums`}>
            {displayDateUS(row.evaluationDate) || "—"}
          </span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        className: EVALUATION_COL.status,
        render: (row) => {
          const status = String(row.status || "draft").toLowerCase();
          const isCompleted = status === "completed";
          return (
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${
                isCompleted
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
            >
              {status === "completed" ? "Completed" : "Draft"}
            </span>
          );
        },
      },
      {
        header: "Actions",
        accessor: "actions",
        className: EVALUATION_COL.actions,
        render: (row) => (
          <div className="inline-flex items-center justify-end gap-1.5 flex-nowrap">
            <button
              type="button"
              onClick={() => onEdit(row)}
              className={EVALUATION_BTN.soft}
              title="Edit evaluation"
              aria-label="Edit evaluation"
            >
              <EditIcon size={13} />
              Edit
            </button>
            {isAdmin ? (
              <button
                type="button"
                onClick={() => handleDeleteClick(row)}
                className={EVALUATION_BTN.danger}
                title="Delete evaluation"
                aria-label="Delete evaluation"
              >
                <DeleteIcon size={13} />
                Delete
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [onEdit, isAdmin],
  );

  const tableSelection = {
    enabled: true,
    isSelected: (row) => selection.isSelected(getEvaluationRowKey(row)),
    isSelectable: (row) => evaluationPrintable(row),
    onToggleRow: (row, _index, checked) =>
      selection.toggleRow(getEvaluationRowKey(row), checked),
    allSelected: selection.allSelectableSelected,
    someSelected: selection.someSelectableSelected,
    onToggleAll: selection.toggleAllSelectable,
  };

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1
            className={`text-2xl font-bold ${EVALUATION_THEME.section.title}`}
          >
            Evaluation History
          </h1>
          <p className={PAGE_SUBTITLE}>
            View and manage past performance evaluations
          </p>
        </div>
        <div className={PAGE_ACTIONS}>
          <button
            type="button"
            onClick={() => onBack()}
            className={EVALUATION_BTN.primary}
          >
            <PlusIcon size={16} />
            New Form
          </button>
        </div>
      </div>

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${EVALUATION_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${EVALUATION_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${EVALUATION_THEME.section.bar}`}
            aria-hidden
          />
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${EVALUATION_THEME.section.title}`}
          >
            Evaluations
          </h2>
          <span
            className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${EVALUATION_THEME.section.badge}`}
          >
            {displayedEvaluationsList.length}
            {hasActiveFilters || searchTerm.trim() ? ` / ${totalItems}` : ""}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by employee, type, position..."
              className={EVALUATION_FIELD.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="relative">
              <label className="sr-only" htmlFor="eval-type-filter">
                Filter by type
              </label>
              <select
                id="eval-type-filter"
                className={`${EVALUATION_FIELD.select} min-w-[120px] ${
                  typeFilter ? EVALUATION_FIELD.selectActive : ""
                }`}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                {TYPE_FILTERS.map((option) => (
                  <option key={option.value || "all-type"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            <div className="relative">
              <label className="sr-only" htmlFor="eval-status-filter">
                Filter by status
              </label>
              <select
                id="eval-status-filter"
                className={`${EVALUATION_FIELD.select} min-w-[130px] ${
                  statusFilter ? EVALUATION_FIELD.selectActive : ""
                }`}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTERS.map((option) => (
                  <option
                    key={option.value || "all-status"}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className={EVALUATION_BTN.clear}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <div className="px-3 pt-2.5">
          <DocumentBulkActions
            selectedCount={selection.selectedCount}
            loading={bulkPrintLoading}
            onPrint={handleBulkPrint}
          />
        </div>

        <Table
          columns={columns}
          data={displayedEvaluationsList}
          loading={loading}
          keyField="__rowKey"
          showSrNo={false}
          density="compact"
          embedded
          tableClassName="table-fixed"
          headTone={EVALUATION_THEME.tableHead}
          sortKey={evaluationSortKey}
          sortOrder={evaluationSortOrder}
          onSort={toggleEvaluationSort}
          selection={tableSelection}
          pagination={{
            currentPage: page,
            totalPages: totalPages,
            totalItems: totalItems,
            itemsPerPage: limit,
            onPageChange: setPage,
          }}
          noDataMessage="No evaluations found."
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Evaluation"
        message={`Are you sure you want to delete the ${
          itemToDelete?.type || ""
        } evaluation for ${itemToDelete?.employee?.firstName || ""} ${
          itemToDelete?.employee?.lastName || ""
        }?`}
        isLoading={isDeleting}
      />
    </div>
  );
};

EvaluationHistory.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default EvaluationHistory;
